import React, { useEffect, useState, useRef, useMemo } from 'react'
import styled from 'styled-components'
import ConfigSlider from '@components/Form/ConfigSlider'
import { Switch, Tooltip } from '@mui/material'
import { useLanguage } from '@/contexts/LanguageContext'

// Styled components
const Container = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 12px;
    color: #fff;
`

const GroupContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 0px;
    width: 100%;
    box-sizing: border-box;
`

const GroupTitle = styled.h4`
    margin: 0 0 10px 0;
    color: #6366f1;
    font-size: 0.85rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    font-family: var(--font-chakra);
    opacity: 0.9;
`

const ToggleSwitch = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 8px 0;
`

const ToggleLabel = styled.span`
    font-size: 0.75rem;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.7);
    display: flex;
    align-items: center;
    gap: 8px;
    letter-spacing: 0.1px;
`

const SwitchInput = styled.input`
    position: relative;
    width: 40px;
    height: 20px;
    appearance: none;
    background: rgba(255, 255, 255, 0.1);
    outline: none;
    border-radius: 20px;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    transition: 0.3s;
    opacity: ${({ disabled }) => (disabled ? 0.3 : 1)};
    pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

    &:checked {
        background: #6366f1;
    }

    &::before {
        content: '';
        position: absolute;
        width: 16px;
        height: 16px;
        border-radius: 50%;
        top: 2px;
        left: 2px;
        background: #fff;
        transform: scale(1);
        transition: 0.3s;
    }

    &:checked::before {
        left: 22px;
    }
`

// Help icon usage in toggle needs to be manual since ConfigSlider doesn't handle boolean toggles directly yet
// Or we could adapt ConfigSlider to handle booleans, but for now let's keep boolean logic separate but styled similarly
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'

const LabelWithTooltip = ({ commandKey, metadata }) => {
    const meta = metadata[commandKey] || { label: commandKey, desc: '' }
    return (
        <>
            <span>{meta.label}</span>
            <Tooltip title={meta.desc} placement="right" arrow>
                <HelpOutlineIcon
                    style={{
                        fontSize: 14,
                        color: 'rgba(255,255,255,0.4)',
                        cursor: 'help',
                    }}
                />
            </Tooltip>
        </>
    )
}

// Metadata for commands
const COMMAND_METADATA = (t) => ({
    cl_crosshairsize: { label: t('SIZE'), desc: t('SIZE_DESC') },
    cl_crosshairthickness: { label: t('THICKNESS'), desc: t('THICKNESS_DESC') },
    cl_crosshairgap: { label: t('GAP'), desc: t('GAP_DESC') },
    cl_crosshairdot: { label: t('CENTER_DOT'), desc: t('CENTER_DOT_DESC') },
    cl_crosshairstyle: { label: t('STYLE'), desc: t('STYLE_DESC') },
    cl_crosshairalpha: { label: t('OPACITY'), desc: t('OPACITY_DESC') },
    cl_crosshair_drawoutline: { label: t('OUTLINE'), desc: t('OUTLINE_DESC') },
    cl_crosshair_outlinethickness: {
        label: t('OUTLINE_THICKNESS'),
        desc: t('OUTLINE_THICKNESS_DESC'),
    },
    cl_crosshaircolor_r: { label: t('RED'), desc: t('RED_DESC') },
    cl_crosshaircolor_g: { label: t('GREEN'), desc: t('GREEN_DESC') },
    cl_crosshaircolor_b: { label: t('BLUE'), desc: t('BLUE_DESC') },
    cl_crosshair_t: { label: t('T_CROSSHAIR'), desc: t('T_CROSSHAIR_DESC') },
    cl_crosshair_sniper_width: {
        label: t('SNIPER_WIDTH'),
        desc: t('SNIPER_WIDTH_DESC'),
    },
})

const GROUPS = [
    {
        title: 'GENERAL',
        keys: [
            'cl_crosshairsize',
            'cl_crosshairthickness',
            'cl_crosshairgap',
            'cl_crosshair_outlinethickness',
            'cl_crosshair_sniper_width',
            'cl_crosshairdot',
            'cl_crosshair_drawoutline',
            'cl_crosshairstyle',
            'cl_crosshair_recoil',
        ],
    },
    {
        title: 'COLOR',
        keys: [
            'cl_crosshaircolor',
            'cl_crosshaircolor_r',
            'cl_crosshaircolor_g',
            'cl_crosshaircolor_b',
            'cl_crosshairalpha',
        ],
    },
    {
        title: 'CS2_ENHANCEMENTS',
        keys: ['cl_crosshair_t'],
    },
]

const TOGGLE_KEYS = [
    'cl_crosshairdot',
    'cl_crosshair_drawoutline',
    'cl_crosshair_t',
]

const QUICK_VALUES = {
    cl_crosshairsize: [1, 1.5, 2, 2.5, 3],
    cl_crosshairthickness: [0.5, 1, 1.5],
    cl_crosshairgap: [-3, -2, -1, 0, 1],
    cl_crosshair_outlinethickness: [0, 0.5, 1],
    cl_crosshairalpha: [200, 255],
    cl_crosshairstyle: [4, 5],
    cl_crosshair_sniper_width: [1, 2, 3],
}

const CommandInput = ({
    commandString,
    onChange,
    showGroups = ['GENERAL', 'COLOR', 'OUTLINE_GROUP', 'CS2_ENHANCEMENTS'],
    mergeGroups = false,
    excludeKeys = [],
    disabled = false
}) => {
    const { t } = useLanguage()
    const metadata = COMMAND_METADATA(t)
    const [commands, setCommands] = useState([])
    const lastEmittedString = useRef('')
    // Ref mirror of commands — always current even when state update is still pending
    const commandsRef = useRef([])

    const refinedOptions = {
        cl_crosshairsize: { min: 0, max: 20, step: 0.5 },
        cl_crosshairthickness: { min: 0.5, max: 10, step: 0.5 },
        cl_crosshairgap: { min: -20, max: 20, step: 1 },
        cl_crosshair_outlinethickness: { min: 0, max: 3, step: 0.5 },
        cl_crosshairalpha: { min: 0, max: 255, step: 5 },
        cl_crosshaircolor_r: { min: 0, max: 255, step: 1 },
        cl_crosshaircolor_g: { min: 0, max: 255, step: 1 },
        cl_crosshaircolor_b: { min: 0, max: 255, step: 1 },
        cl_crosshairdot: { min: 0, max: 1, step: 1 },
        cl_crosshair_drawoutline: { min: 0, max: 1, step: 1 },
        cl_crosshairstyle: { min: 0, max: 5, step: 1 },
        cl_crosshair_t: { min: 0, max: 1, step: 1 },
        cl_crosshair_sniper_width: { min: 1, max: 10, step: 1 },
    }

    const parseCommands = (str) => {
        if (!str) return []
        const allKeys = GROUPS.reduce((acc, g) => [...acc, ...g.keys], [])

        return str
            .split(';')
            .map((cmd) => {
                const parts = cmd.trim().split(' ')
                if (parts.length < 2) return null
                const key = parts[0]
                const value = parts.slice(1).join(' ')

                // Only keep commands that this component is supposed to manage
                if (!allKeys.includes(key)) return null

                return { key, value: parseFloat(value) || 0 }
            })
            .filter((cmd) => cmd && cmd.key)
    }

    const commandsToString = (cmds) => {
        return cmds.map((cmd) => `${cmd.key} ${cmd.value}`).join('; ') + ';'
    }

    useEffect(() => {
        if (commandString !== lastEmittedString.current) {
            const newCommands = parseCommands(commandString)
            commandsRef.current = newCommands
            setCommands(newCommands)
        }
    }, [commandString])

    const updateCommands = (updates) => {
        // Use ref so we always read the latest commands even if the state update
        // from a previous interaction hasn't been committed yet (e.g. rapid slider drags)
        const base = commandsRef.current.length > 0 ? commandsRef.current : commands
        const newCommands = base.map((cmd) => {
            const update = updates.find((u) => u.key === cmd.key)
            return update ? { ...cmd, value: update.value } : cmd
        })

        commandsRef.current = newCommands
        setCommands(newCommands)

        const newString = commandsToString(newCommands)
        lastEmittedString.current = newString
        if (onChange) onChange(newString)
    }

    const handleChange = (commandKey, newValue) => {
        const options = refinedOptions[commandKey] || {
            min: 0,
            max: 100,
            step: 1,
        }
        const { min, max } = options
        let parsedValue = parseFloat(newValue)
        if (isNaN(parsedValue)) return

        parsedValue = Math.min(Math.max(parsedValue, min), max)

        const updates = [{ key: commandKey, value: parsedValue }]

        // Rule 1: If Outline Thickness is 0, disable Draw Outline
        if (commandKey === 'cl_crosshair_outlinethickness' && parsedValue === 0) {
            updates.push({ key: 'cl_crosshair_drawoutline', value: 0 })
        }

        // Rule 2: If enabling Draw Outline and thickness is 0, set thickness to 0.5
        if (commandKey === 'cl_crosshair_drawoutline' && parsedValue === 1) {
            const currentThick = commands.find(c => c.key === 'cl_crosshair_outlinethickness')?.value || 0
            if (currentThick === 0) {
                updates.push({ key: 'cl_crosshair_outlinethickness', value: 0.5 })
            }
        }

        // Rule 3: If Outline Thickness > 0, ensure Draw Outline is enabled
        if (commandKey === 'cl_crosshair_outlinethickness' && parsedValue > 0) {
            const currentOutline = commands.find(c => c.key === 'cl_crosshair_drawoutline')?.value || 0
            if (currentOutline === 0) {
                updates.push({ key: 'cl_crosshair_drawoutline', value: 1 })
            }
        }

        updateCommands(updates)
    }

    const renderControl = (key, command) => {
        if (excludeKeys.includes(key)) return null

        const options = refinedOptions[key]
        if (!options) return null

        if (excludeKeys.includes(key)) return null
        if (TOGGLE_KEYS.includes(key)) {
            const isChecked = command.value === 1
            const meta = metadata[key] || { label: key, desc: '' }

            return (
                <ToggleSwitch key={key} style={{ opacity: disabled ? 0.5 : 1 }}>
                    <ToggleLabel>
                        {meta.label}
                        <Tooltip title={meta.desc} placement="right" arrow>
                            <HelpOutlineIcon
                                style={{
                                    fontSize: 14,
                                    color: 'rgba(255,255,255,0.4)',
                                    cursor: 'help',
                                }}
                            />
                        </Tooltip>
                    </ToggleLabel>
                    <SwitchInput
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) =>
                            !disabled && handleChange(key, e.target.checked ? 1 : 0)
                        }
                        disabled={disabled}
                    />
                </ToggleSwitch>
            )
        }

        const quickVals = QUICK_VALUES[key]
        return (
            <ConfigSlider
                key={key}
                label={metadata[key]?.label || key}
                description={metadata[key]?.desc}
                min={options.min}
                max={options.max}
                step={options.step}
                value={command.value}
                onChange={(val) => !disabled && handleChange(key, val)}
                presets={quickVals || []}
                showPresetsInline={true}
                presetsPlacement="header"
                disabled={disabled}
            />
        )
    }

    // Filter logic
    const filteredGroups = GROUPS.filter((g) => showGroups.includes(g.title))

    if (mergeGroups) {
        // Collect all keys from filtered groups
        const mergedKeys = filteredGroups.reduce(
            (acc, group) => [...acc, ...group.keys],
            []
        )
        return (
            <Container>
                <GroupContainer>
                    {mergedKeys.map((key) => {
                        const command = commands.find((c) => c.key === key)
                        if (!command) return null
                        return renderControl(key, command)
                    })}
                </GroupContainer>
            </Container>
        )
    }

    return (
        <Container>
            {filteredGroups.map((group) => (
                <GroupContainer key={group.title}>
                    <GroupTitle>{t(group.title)}</GroupTitle>
                    {group.keys.map((key) => {
                        const command = commands.find((c) => c.key === key)
                        if (!command) return null
                        return renderControl(key, command)
                    })}
                </GroupContainer>
            ))}
        </Container>
    )
}

export default CommandInput
